/**
 * CollabBoard – «Legg inn ledig» tab.
 * Ett innebygd skjema for å markere seg selv som ledig + liste over andre som er ledige.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import MultiDatePicker from "./MultiDatePicker";
import { formatFollowers } from "./CollabAccounts";

const TYPES = ["sfs", "mass", "l4l", "story_swap"];
const PLATFORMS = ["onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other"];

export default function CollabBoard() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  // Form-state
  const [type, setType] = useState("sfs");
  const [platforms, setPlatforms] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [wantedDates, setWantedDates] = useState([]);
  const [initialDates, setInitialDates] = useState([]); // hva som lå inne ved valg – for diff ved lagring
  const [audMin, setAudMin] = useState("");
  const [audMax, setAudMax] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const load = () => {
    api.get("/collab/board")
      .then((r) => setItems(r.data?.results || []))
      .catch(() => setItems([]));
  };

  useEffect(() => {
    load();
    api.get("/me/collab-accounts").then((r) => setAccounts(r.data?.results || [])).catch(() => {});
  }, []);

  // Forhåndsutfyll fra eksisterende slots når type/kontoer/plattformer endres
  useEffect(() => {
    if (accounts.length === 0 && platforms.length === 0 && accountIds.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        // Bredt datointervall: 6 mnd tilbake til 12 mnd frem
        const today = new Date();
        const start = new Date(today); start.setMonth(start.getMonth() - 6);
        const end = new Date(today); end.setMonth(end.getMonth() + 12);
        const fmt = (d) => d.toISOString().slice(0, 10);
        const r = await api.get(`/collab/calendar/me?start=${fmt(start)}&end=${fmt(end)}`);
        if (cancelled) return;
        const slots = r.data?.slots || [];
        const matching = slots.filter((s) => {
          if (!s.is_available) return false;
          if (!(s.types || []).includes(type)) return false;
          const slotAccounts = s.account_ids || [];
          const slotPlats = s.platforms || [];
          const accountMatch = accountIds.length === 0 || accountIds.some((id) => slotAccounts.includes(id));
          const platMatch = platforms.length === 0 || platforms.some((p) => slotPlats.includes(p));
          // Hvis brukeren ikke har valgt noen konto/plattform: vis alle av denne typen
          if (accountIds.length === 0 && platforms.length === 0) return true;
          return accountMatch || platMatch;
        });
        const dates = matching.map((m) => m.date).sort();
        setWantedDates(dates);
        setInitialDates(dates);
        // Forhåndsutfyll fra første treff
        if (matching.length > 0) {
          if (matching[0].note) setDescription(matching[0].note);
        }
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [type, JSON.stringify(accountIds), JSON.stringify(platforms), accounts.length]); // eslint-disable-line

  const accountsByPlatform = accounts.reduce((acc, a) => {
    (acc[a.platform] = acc[a.platform] || []).push(a);
    return acc;
  }, {});
  const platformsWithoutAccounts = PLATFORMS.filter((p) => !accountsByPlatform[p]);

  const toggleAccount = (id) => setAccountIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const togglePlatform = (p) => setPlatforms(platforms.includes(p) ? platforms.filter((x) => x !== p) : [...platforms, p]);

  const updateDates = async (arr) => {
    setWantedDates(arr);
    if (arr.length === 0) { setConflicts([]); return; }
    try {
      const r = await api.get(`/collab/check-conflicts?dates=${arr.join(",")}&type=${type}`);
      setConflicts(r.data?.conflicts || []);
    } catch {
      setConflicts([]);
    }
  };

  // Re-sjekk konflikter når type byttes (samme datoer kan kollidere med en type men ikke en annen)
  useEffect(() => {
    if (wantedDates.length === 0) { setConflicts([]); return; }
    let cancelled = false;
    api.get(`/collab/check-conflicts?dates=${wantedDates.join(",")}&type=${type}`)
      .then((r) => { if (!cancelled) setConflicts(r.data?.conflicts || []); })
      .catch(() => { if (!cancelled) setConflicts([]); });
    return () => { cancelled = true; };
  }, [type]); // eslint-disable-line

  const resetForm = () => {
    setType("sfs");
    setPlatforms([]);
    setAccountIds([]);
    setWantedDates([]);
    setAudMin("");
    setAudMax("");
    setDescription("");
    setConflicts([]);
  };

  const save = async () => {
    if (wantedDates.length === 0) {
      toast.error(t("collab.err_pick_dates"));
      return;
    }
    // Plattformer dekkes av enten valgte konto-IDs eller "andre plattformer uten konto"
    const platformsFromAccounts = accountIds
      .map((id) => accounts.find((a) => a.id === id)?.platform)
      .filter(Boolean);
    const allPlatforms = Array.from(new Set([...platformsFromAccounts, ...platforms]));
    if (allPlatforms.length === 0) {
      toast.error(t("collab.err_pick_platform"));
      return;
    }
    if (conflicts.length > 0) {
      const dates = conflicts.map((c) => c.scheduled_date).join(", ");
      if (!window.confirm(t("collab.confirm_conflict", { dates }))) return;
    }
    setSaving(true);
    try {
      await api.post("/collab/board", {
        type,
        platforms: allPlatforms,
        account_ids: accountIds,
        wanted_dates: wantedDates,
        audience_size_min: audMin ? parseInt(audMin, 10) : null,
        audience_size_max: audMax ? parseInt(audMax, 10) : null,
        nationalities: [],
        kinks: [],
        description,
      });
      // Slett datoer som var inne ved start, men er fjernet av brukeren
      const removed = initialDates.filter((d) => !wantedDates.includes(d));
      for (const d of removed) {
        try { await api.delete(`/collab/calendar/me/${d}`); } catch {/* ignore */}
      }
      setInitialDates(wantedDates);
      toast.success(t("collab.available_published"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="collab-board" className="space-y-6">
      <header>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("collab.available_title_v2", "Legg til / rediger ledig")}</h2>
        <p className="text-xs text-[var(--cream-faint)]">{t("collab.available_intro_v2", "Velg først type og kontoer/plattformer — eksisterende ledige datoer for det valget vises i kalenderen og kan redigeres direkte.")}</p>
      </header>

      {/* Innebygd skjema */}
      <div className="panel-tight p-5 space-y-4" data-testid="board-form">
        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.type_label")}</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="input-editorial w-full" data-testid="board-form-type">
            {TYPES.map((tp) => <option key={tp} value={tp}>{tp.toUpperCase()}</option>)}
          </select>
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.accounts_platforms_label")}</label>
          {accounts.length === 0 ? (
            <p className="text-xs text-[var(--cream-faint)] mb-2">{t("collab.no_accounts_hint")}</p>
          ) : (
            <div className="space-y-2 mb-2">
              {Object.entries(accountsByPlatform).map(([plat, list]) => (
                <div key={plat}>
                  <div className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] mb-1">{plat.toUpperCase()}</div>
                  <div className="flex flex-wrap gap-1">
                    {list.map((a) => (
                      <button key={a.id} onClick={() => toggleAccount(a.id)} type="button"
                        data-testid={`board-account-${a.id}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 font-meta text-[11px] border ${accountIds.includes(a.id) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                        @{a.handle}
                        {a.label && <span className="opacity-60">·{a.label}</span>}
                        <span className="inline-flex items-center gap-0.5 opacity-70">
                          <Users className="h-2.5 w-2.5" />
                          {formatFollowers(a.follower_count)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {platformsWithoutAccounts.length > 0 && (
            <>
              <div className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] mt-3 mb-1">{t("collab.other_platforms_no_account")}</div>
              <div className="flex flex-wrap gap-1">
                {platformsWithoutAccounts.map((p) => (
                  <button key={p} onClick={() => togglePlatform(p)} type="button"
                    data-testid={`board-platform-${p}`}
                    className={`px-3 py-1.5 font-meta text-[11px] border ${platforms.includes(p) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                    {p.toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.pick_dates")}</label>
          <MultiDatePicker value={wantedDates} onChange={updateDates} data-testid="board-dates" />
          <p className="text-xs text-[var(--cream-faint)] mt-1">
            {initialDates.length > 0
              ? t("collab.pick_dates_hint_edit", "Eksisterende datoer for dette valget er forhåndsutfylt. Legg til nye, eller klikk for å fjerne.")
              : t("collab.pick_dates_hint_board")}
          </p>
        </div>

        {conflicts.length > 0 && (
          <div className="panel-tight p-3 border border-amber-500/40 bg-amber-500/10" data-testid="conflict-warning">
            <div className="font-meta text-[10px] tracking-[0.2em] text-amber-300 mb-1.5">{t("collab.conflict_title")}</div>
            <ul className="text-xs text-[var(--cream)] space-y-0.5">
              {conflicts.map((c, i) => (
                <li key={i}>• {c.scheduled_date} — {c.type?.toUpperCase()} ({c.status})</li>
              ))}
            </ul>
            <p className="text-xs text-[var(--cream-faint)] mt-1.5">{t("collab.conflict_hint")}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.audience_min")}</label>
            <input type="number" value={audMin} onChange={(e) => setAudMin(e.target.value)} className="input-editorial w-full" data-testid="board-audmin" />
          </div>
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.audience_max")}</label>
            <input type="number" value={audMax} onChange={(e) => setAudMax(e.target.value)} className="input-editorial w-full" data-testid="board-audmax" />
          </div>
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("collab.description_label")}</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1000}
            className="input-editorial w-full font-manrope text-sm" data-testid="board-description" />
        </div>

        <button onClick={save} disabled={saving} className="btn-magenta w-full disabled:opacity-40" data-testid="board-publish">
          {saving ? t("collab.saving") : t("collab.save", "Lagre")}
        </button>
      </div>

      {/* Liste over andre som er ledige */}
      <div>
        <h3 className="font-fraunces italic text-lg text-[var(--cream)] mb-3">{t("collab.others_available", "Andre som er ledige")}</h3>
        {items.length === 0 ? (
          <div className="panel-tight p-8 text-center text-[var(--cream-soft)] font-fraunces italic">
            {t("collab.no_board_posts")}
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((p) => (
              <li key={p.id} className="panel-tight p-4" data-testid={`board-${p.id}`}>
                <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                  <div>
                    <h4 className="font-fraunces italic text-lg text-[var(--cream)]">{p.creator_username}</h4>
                    <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mt-1">
                      {p.type.toUpperCase()}
                      {p.wanted_dates && p.wanted_dates.length > 1
                        ? ` · ${p.wanted_dates.length} ${t("collab.dates_count")}`
                        : ` · ${p.wanted_date_start}${p.wanted_date_end && p.wanted_date_end !== p.wanted_date_start ? `–${p.wanted_date_end}` : ""}`}
                    </div>
                    {p.wanted_dates && p.wanted_dates.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {p.wanted_dates.map((d) => (
                          <span key={d} className="text-[10px] border border-white/15 text-[var(--cream-soft)] px-1.5 py-0.5">{d}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {p.platforms.map((pl) => (
                      <span key={pl} className="font-meta text-[10px] px-2 py-0.5 border border-white/15 text-[var(--cream-soft)]">
                        {pl.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
                {p.description && <p className="text-sm text-[var(--cream-soft)] whitespace-pre-line mb-2">{p.description}</p>}
                {(p.audience_size_min || p.audience_size_max) && (
                  <div className="text-xs text-[var(--cream-faint)]">
                    {t("collab.audience")}: {p.audience_size_min || "?"}–{p.audience_size_max || "?"}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
